/**
 * MeMark Universal Credential Parser & Formatter
 * 
 * Supports:
 * - Structured JSON: { email, username, password, activationCode, notes }
 * - Multi-line key-value strings (email: ..., user: ..., password: ..., code: ...)
 * - Single-line credentials (user:pass, email:pass)
 * - Raw passwords & API tokens
 * - Resilient cleanup of legacy / corrupted strings (e.g. `{"username"`, `{"email"`, `email`)
 */

export function parseCredentialRecord(raw = '') {
  const result = {
    email: '',
    username: '',
    password: '',
    activationCode: '',
    notes: '',
    raw: String(raw || ''),
  };

  if (!raw || typeof raw !== 'string') return result;
  const str = raw.trim();
  if (!str) return result;

  // 1. Try parsing as JSON first
  if (str.startsWith('{') && str.endsWith('}')) {
    try {
      const parsed = JSON.parse(str);
      if (typeof parsed === 'object' && parsed !== null) {
        result.email = String(parsed.email || parsed.mail || '').trim();
        result.username = String(parsed.username || parsed.user || parsed.login || '').trim();
        result.password = String(parsed.password || parsed.pass || parsed.pwd || parsed.secret || '').trim();
        result.activationCode = String(
          parsed.activationCode ||
          parsed.activation_code ||
          parsed.activation ||
          parsed.code ||
          parsed.license ||
          parsed.key ||
          parsed.totp ||
          parsed.pin ||
          ''
        ).trim();
        result.notes = String(parsed.notes || parsed.memo || '').trim();

        // Cross-fill username/email if only one is specified
        if (!result.username && result.email) result.username = result.email;
        if (!result.email && result.username && result.username.includes('@')) result.email = result.username;
        return result;
      }
    } catch (_) {
      // If JSON.parse fails, fall through to line-by-line parser
    }
  }

  // 2. Multi-line or comma-separated key:value analysis
  const lines = str.split(/[\r\n,]+/);
  let matchedExplicitFields = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Email
    const emailMatch = trimmed.match(/^(?:\{?"?email"?|\{?"?e-mail"?|\{?"?mail"?)\s*[:=]\s*"?([^"\r\n}]+)"?/i);
    if (emailMatch) {
      result.email = emailMatch[1].trim();
      matchedExplicitFields = true;
      continue;
    }

    // Username
    const userMatch = trimmed.match(/^(?:\{?"?username"?|\{?"?user"?|\{?"?login"?|\{?"?handle"?)\s*[:=]\s*"?([^"\r\n}]+)"?/i);
    if (userMatch) {
      result.username = userMatch[1].trim();
      matchedExplicitFields = true;
      continue;
    }

    // Password
    const passMatch = trimmed.match(/^(?:\{?"?password"?|\{?"?pass"?|\{?"?pwd"?|\{?"?secret"?)\s*[:=]\s*"?([^"\r\n}]+)"?/i);
    if (passMatch) {
      result.password = passMatch[1].trim();
      matchedExplicitFields = true;
      continue;
    }

    // Activation Code / 2FA / License
    const codeMatch = trimmed.match(
      /^(?:\{?"?activation(?:_code)?"?|\{?"?activation"?|\{?"?code"?|\{?"?license"?|\{?"?key"?|\{?"?token"?|\{?"?pin"?|\{?"?totp"?)\s*[:=]\s*"?([^"\r\n}]+)"?/i
    );
    if (codeMatch) {
      result.activationCode = codeMatch[1].trim();
      matchedExplicitFields = true;
      continue;
    }
  }

  if (matchedExplicitFields) {
    if (!result.username && result.email) result.username = result.email;
    if (!result.email && result.username && result.username.includes('@')) result.email = result.username;
    return result;
  }

  // 3. Single-line delimiter (user:pass or email:pass)
  // Ensure it's not a broken JSON snippet like `{"username"`
  if (!str.startsWith('{') && lines.length === 1) {
    const colonIdx = str.indexOf(':');
    if (colonIdx > 0 && colonIdx < str.length - 1) {
      const part1 = str.slice(0, colonIdx).trim();
      const part2 = str.slice(colonIdx + 1).trim();

      // Guard against keywords
      if (!/^(?:email|username|user|password|pass|pwd|code|activation)$/i.test(part1)) {
        if (part1.includes('@')) {
          result.email = part1;
          result.username = part1;
        } else {
          result.username = part1;
        }
        result.password = part2;
        return result;
      } else {
        if (/^(?:email|mail)$/i.test(part1)) {
          result.email = part2;
          result.username = part2;
          return result;
        }
        if (/^(?:username|user|login)$/i.test(part1)) {
          result.username = part2;
          return result;
        }
        if (/^(?:password|pass|pwd)$/i.test(part1)) {
          result.password = part2;
          return result;
        }
      }
    }
  }

  // 4. Fallback: single raw string (e.g. just a password or token)
  result.password = str;
  return result;
}

export function formatCredentialRecord({
  email = '',
  username = '',
  password = '',
  activationCode = '',
  notes = '',
}) {
  const obj = {};
  if (email && email.trim()) obj.email = email.trim();
  if (username && username.trim()) obj.username = username.trim();
  if (password && password.trim()) obj.password = password.trim();
  if (activationCode && activationCode.trim()) obj.activationCode = activationCode.trim();
  if (notes && notes.trim()) obj.notes = notes.trim();

  // Return clean formatted JSON
  return JSON.stringify(obj, null, 2);
}
